/**
 * PostHog Provider — initializes PostHog on China/global web and app WebView.
 */
import { useEffect, useRef, useState, createContext, useContext, ReactNode } from "react";
import config from "@Config/index";
import useIsApp from "hooks/useIsApp";
import { getPostHogConfig, isPostHogEnabled } from "./posthogConfig";
import { registerPostHogSuperProperties, setPostHogInstance } from "./posthogEventEmitter";

type PostHogInstance = any;

interface PostHogContextValue {
  posthog: PostHogInstance | null;
  isInitialized: boolean;
}

const PostHogContext = createContext<PostHogContextValue>({
  posthog: null,
  isInitialized: false,
});

interface PostHogProviderProps {
  children: ReactNode;
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  const [posthog, setPosthog] = useState<PostHogInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const isAmberApp = useIsApp();
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (!isPostHogEnabled() || initStartedRef.current) {
      return;
    }

    initStartedRef.current = true;
    const posthogConfig = getPostHogConfig();

    const initPostHog = async () => {
      try {
        // eslint-disable-next-line import/no-extraneous-dependencies
        const posthogModule = await import("posthog-js");
        const posthogInstance = posthogModule.default;

        posthogInstance.init(posthogConfig.apiKey, {
          api_host: posthogConfig.host,
          autocapture: false,
          capture_pageview: true,
          capture_pageleave: true,
          capture_performance: false,
          capture_dead_clicks: false,
          rageclick: false,
          disable_session_recording: true,
          disable_surveys: true,
          enable_heatmaps: false,
          loaded: (ph) => {
            setPostHogInstance(ph);
            registerPostHogSuperProperties().catch(() => undefined);

            let lastAttributedFcmToken: string | null = null;

            const attributeFcmToken = (token: string) => {
              if (token && token !== lastAttributedFcmToken) {
                lastAttributedFcmToken = token;
                ph.setPersonProperties({ fcm_token: token });
              }
            };

            const existingToken = (window as any).fcmToken;
            if (existingToken) {
              attributeFcmToken(existingToken);
            } else {
              let _fcmToken: string | null = null;
              Object.defineProperty(window, "fcmToken", {
                get: () => _fcmToken,
                set: (value: string) => {
                  _fcmToken = value;
                  attributeFcmToken(value);
                },
                configurable: true,
                enumerable: true,
              });
            }

            if (!config.isProduction) {
              // eslint-disable-next-line testing-library/no-debug
              ph.debug();
            }
          },
        });

        setPostHogInstance(posthogInstance);
        setPosthog(posthogInstance);
        setIsInitialized(true);

        import("./posthogTestEvents");
      } catch (error) {
        console.error("Failed to initialize PostHog:", error);
        initStartedRef.current = false;
      }
    };

    initPostHog();
  }, [isAmberApp]);

  return (
    <PostHogContext.Provider value={{ posthog, isInitialized }}>{children}</PostHogContext.Provider>
  );
};

export const usePostHog = (): PostHogInstance | null => {
  const { posthog } = useContext(PostHogContext);
  return posthog;
};

export const usePostHogInitialized = (): boolean => {
  const { isInitialized } = useContext(PostHogContext);
  return isInitialized;
};

export default PostHogProvider;
